import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { RailLayout } from '@/components/RailLayout';

interface MailSplitPaneProps {
  sidebar: ReactNode;
  list: ReactNode;
  detail: ReactNode;
  detailActive: boolean;
}

/**
 * List and reader share one cell of a single-cell grid; whichever is inactive
 * is made invisible + inert rather than `display:none`. A `display:none`
 * subtree loses its layout, and with it the browser's own scroll offset and
 * every measurement scroll restoration needs — so opening a reader used to
 * throw the list's position away. `visibility:hidden` keeps the inactive pane
 * laid out (scroll offset retained natively, still measurable), while `inert`
 * removes it from focus, hit-testing and the accessibility tree exactly like
 * `display:none` did.
 */
export function MailSplitPane({
  sidebar,
  list,
  detail,
  detailActive,
}: MailSplitPaneProps) {
  return (
    <RailLayout sidebar={sidebar}>
      <div className="grid flex-1 min-h-0 min-w-0 grid-cols-[minmax(0,1fr)] grid-rows-[minmax(0,1fr)]">
        <div
          className={cn(
            'col-start-1 row-start-1 flex min-h-0 min-w-0 flex-col',
            detailActive && 'invisible'
          )}
          inert={detailActive}
          aria-hidden={detailActive}
        >
          {list}
        </div>
        <div
          className={cn(
            'col-start-1 row-start-1 flex min-h-0 min-w-0 flex-col',
            !detailActive && 'invisible'
          )}
          inert={!detailActive}
          aria-hidden={!detailActive}
        >
          {detail}
        </div>
      </div>
    </RailLayout>
  );
}
